import { create } from 'zustand';
import { http } from '@/libs/http';
import { toast } from 'sonner';
import type { Group } from '@/types';

interface GroupState {
    groups: Group[];
    isLoading: boolean;
    error: string | null;
    
    // Core actions
    list: (filter?: (groups: Group[]) => Group[]) => Promise<Group[]>;
    get: (id: string) => Promise<Group>;
    create: (data: Partial<Group>) => Promise<void>;
    update: (id: string, data: Partial<Group>) => Promise<void>;
    delete: (id: string) => Promise<void>;
    select: (criteria: (groups: Group[]) => Group) => Group;

    // Additional actions
    addMember: (groupId: string, userId: string) => Promise<void>;
    removeMember: (groupId: string, userId: string) => Promise<void>;
    listUsers: (groupId: string) => Promise<any[]>;
    listRoles: (groupId: string) => Promise<any[]>;
    addRole: (groupId: string, roleId: string) => Promise<void>;
    removeRole: (groupId: string, roleId: string) => Promise<void>;
}

export const useGroups = create<GroupState>((set, get) => ({
    groups: [],
    isLoading: false,
    error: null,

    list: async (filter) => {
        set({ isLoading: true, error: null });
        try {
            const response = await http.get<any>('/mgmt/v1/groups');
            const data = Array.isArray(response) ? response : (response.items || []);
            set({ groups: data, isLoading: false });
            return filter ? filter(data) : data;
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to load groups';
            set({ error: errorMsg, isLoading: false });
            toast.error(errorMsg);
            return [];
        }
    },

    get: async (id) => {
        const found = get().groups.find((group) => group.id === id);
        if (found) return found;
        const data = await http.get<Group>(`/mgmt/v1/groups/${id}`);
        set({ groups: [...get().groups.filter((g) => g.id !== id), data] });
        return data;
    },

    create: async (data) => {
        const previousGroups = get().groups;
        const tempId = `temp-${Date.now()}`;
        const tempGroup = { ...data, id: tempId } as Group;
        
        set({ groups: [...previousGroups, tempGroup] });

        try {
            const newGroup = await http.post<Group>('/mgmt/v1/groups', data);
            set({ 
                groups: get().groups.map(g => g.id === tempId ? newGroup : g) 
            });
            toast.success('Group created successfully');
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to create group';
            set({ groups: previousGroups, error: errorMsg });
            toast.error(errorMsg);
            throw err;
        }
    },

    update: async (id, data) => {
        const previousGroups = get().groups;
        set({
            groups: previousGroups.map(g => g.id === id ? { ...g, ...data } : g)
        });

        try {
            const updatedGroup = await http.put<Group>(`/mgmt/v1/groups/${id}`, data);
            set({
                groups: get().groups.map(g => g.id === id ? updatedGroup : g)
            });
            toast.success('Group updated successfully');
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to update group';
            set({ groups: previousGroups, error: errorMsg });
            toast.error(errorMsg);
            throw err;
        }
    },

    delete: async (id) => {
        const previousGroups = get().groups;
        set({
            groups: previousGroups.filter(g => g.id !== id)
        });

        try {
            await http.delete(`/mgmt/v1/groups/${id}`);
            toast.success('Group deleted successfully');
        } catch (err: any) {
            const errorMsg = err.message || 'Failed to delete group';
            set({ groups: previousGroups, error: errorMsg });
            toast.error(errorMsg);
            throw err;
        }
    },

    select: (criteria) => {
        return criteria(get().groups);
    },

    addMember: async (groupId, userId) => {
        await http.post(`/mgmt/v1/groups/${groupId}/members/${userId}`, {});
    },

    removeMember: async (groupId, userId) => {
        await http.delete(`/mgmt/v1/groups/${groupId}/members/${userId}`);
    },

    listUsers: async (groupId) => {
        return http.get(`/mgmt/v1/groups/${groupId}/users`);
    },

    listRoles: async (groupId) => {
        return http.get(`/mgmt/v1/groups/${groupId}/roles`);
    },

    addRole: async (groupId, roleId) => {
        await http.post(`/mgmt/v1/groups/${groupId}/roles/${roleId}`, {});
    },

    removeRole: async (groupId, roleId) => {
        await http.delete(`/mgmt/v1/groups/${groupId}/roles/${roleId}`);
    }
}));

export const useGroup = (id?: string) => {
    const group = useGroups((state) =>
        id ? state.groups.find((item) => item.id === id) ?? null : null,
    );

    const ensureId = (): string => {
        if (!id) throw new Error('Group id is required.');
        return id;
    };

    return {
        group,
        get: () => useGroups.getState().get(ensureId()),
        update: (data: Partial<Group>) => useGroups.getState().update(ensureId(), data),
        delete: () => useGroups.getState().delete(ensureId()),
        users: {
            list: () => useGroups.getState().listUsers(ensureId()),
            create: (userId: string) => useGroups.getState().addMember(ensureId(), userId),
            delete: (userId: string) => useGroups.getState().removeMember(ensureId(), userId),
        },
        roles: {
            list: () => useGroups.getState().listRoles(ensureId()),
            create: (roleId: string) => useGroups.getState().addRole(ensureId(), roleId),
            delete: (roleId: string) => useGroups.getState().removeRole(ensureId(), roleId),
        },
    };
};
